import type { TableExpressionFilterDefinition } from '@cognite/sdk';
import type z from 'zod';

import { useInstanceQuery } from '#core/hooks/fusion/useInstanceQuery.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';

interface QueryProps {
  filter?: TableExpressionFilterDefinition[];
  limit?: number;
}

export const useViewInstances = <T extends DataModelView, SchemaOutputType extends z.output<T['schema']>>(
  source: T,
  { filter = [], limit = 1000 }: QueryProps = {},
) =>
  useInstanceQuery(
    {
      with: {
        [source.externalId]: {
          nodes: {
            filter: {
              and: [{ hasData: [source] }, ...filter],
            },
          },
          limit,
        },
      },
      select: {
        [source.externalId]: {
          sources: [{ source, properties: ['*'] }],
        },
      },
    },
    (data) =>
      data?.items[source.externalId]
        ?.map((item) => {
          const result = source.schema.safeParse(item);
          if (!result.success) {
            console.warn('Failed to parse instance', item, result);
            throw result.error;
          }
          return result.data as SchemaOutputType;
        })
        .filter((it) => it !== undefined),
  );
